'use client'

import React, { ComponentType } from 'react'
import { Game, GameState } from 'types'
import { formattedDate, formattedTime } from 'shared/timeHelper'
import checkLogo from 'shared/imageHelpers'
import Image, { maxScreenSize } from '@/components/ui-other/basic/Image'
import Link from '@/components/router/Link'
import { roundPath, teamPath, tournamentSchedulePath } from 'shared/urlHelpers'
import { isTabletScreen } from 'shared/domHelpers'
import { getExtraStateNames } from 'data/helpers'

const SpecialRatingInfo: ComponentType<{ game: Game }> = ({ game }) => {
  const extraStates = getExtraStateNames(game)

  const info = [
    extraStates,
    game.remarks && game.remarks.length > 0 ? game.remarks : null
  ]
    .filter(Boolean)
    .join(' – ')

  if (!info || info.length == 0) {
    return null
  }

  return (
    <div className="mb-4 bg-gray-200 rounded px-3 py-2">
      <strong>Hinweis: </strong>
      {info}
    </div>
  )
}

export const Scoreboard: ComponentType<{
  game: Game
  hideGameStatusBtn?: boolean
}> = ({ game, hideGameStatusBtn = false }) => {
  const isTablet = isTabletScreen()

  return (
    <div className="mt-8">
      <div className="tik3-flex-scoreboard-caption tik3-scoreboard-caption mb-3">
        <div className="tik3-flex-scoreboard-caption__meta tik3-scoreboard-meta">
          <div className="tik3-scoreboard-affiliations">
            <Link href={tournamentSchedulePath(game.tournament)}>
              {game.tournament.name}
            </Link>
            {' – '}
            <Link href={roundPath(game.tournament, game.round)}>
              {game.round.name}
            </Link>
          </div>
        </div>
        <div className="tik3-flex-scoreboard-caption__state tik3-scoreboard-state">
          <div className="tik3-scoreboard-state-timestamp">
            <span className="tik3-event-item-meta-timestamp-date">
              {formattedDate(game.startsAt)}{' '}
            </span>
            {' – '}
            {game.showTime && (
              <span className="tik3-event-item-meta-timestamp-time">
                {formattedTime(game.startsAt)}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="tikjs-scoreboard-container tik3-scoreboard-container">
        <div className="tik3-scoreboard tik3-multiscoreboard tik3-scoreboard-activestate-post tik3-scoreboard-simple">
          <table>
            {game.state === GameState.PRE && (
              <tbody>
                <tr className="tik3-scoreboard-row1">
                  <td className="tik3-scoreboard-team">
                    <Link
                      href={teamPath(game.homeTeam)}
                      className="flex justify-start items-center mb-2"
                    >
                      <div className="w-12 h-auto mr-5">
                        <Image
                          className="w-full h-full"
                          src={checkLogo(game.homeTeam.logo, 'club')}
                          alt={`Logo ${game.homeTeam.name}`}
                          objectFit="contain"
                          srcSet={[[100, maxScreenSize]]}
                        />
                      </div>
                      <div>{game.homeTeam.name}</div>
                    </Link>
                  </td>
                  <td className="tik3-scoreboard-score tik3-scoreboard-main-score">
                    –
                  </td>
                </tr>
                <tr className="tik3-scoreboard-row2">
                  <td className="tik3-scoreboard-team">
                    <Link
                      href={teamPath(game.awayTeam)}
                      className="flex justify-start items-center mt-2"
                    >
                      <div className="w-12 h-auto mr-5">
                        <Image
                          className="w-full h-full"
                          src={checkLogo(game.awayTeam.logo, 'club')}
                          alt={`Logo ${game.awayTeam.name}`}
                          objectFit="contain"
                          srcSet={[[100, maxScreenSize]]}
                        />
                      </div>
                      <div>{game.awayTeam.name}</div>
                    </Link>
                  </td>
                  <td className="tik3-scoreboard-score tik3-scoreboard-main-score">
                    –
                  </td>
                </tr>
              </tbody>
            )}
            {game.state === GameState.LIVE && (
              <tbody>
                <tr className="tik3-scoreboard-row1">
                  <td className="tik3-scoreboard-team">
                    <Link
                      href={teamPath(game.homeTeam)}
                      className="flex justify-start items-center mb-2"
                    >
                      <div className="w-12 h-auto mr-5">
                        <Image
                          className="w-full h-full"
                          src={checkLogo(game.homeTeam.logo, 'club')}
                          alt={`Logo ${game.homeTeam.name}`}
                          objectFit="contain"
                          srcSet={[[100, maxScreenSize]]}
                        />
                      </div>
                      <div>{game.homeTeam.name}</div>
                    </Link>
                  </td>
                  <td className="tik3-scoreboard-score tik3-scoreboard-main-score">
                    <div className="bg-primary text-white rounded text-center px-2">
                      {game.homeGoals}
                    </div>
                  </td>
                </tr>
                <tr className="tik3-scoreboard-row2">
                  <td className="tik3-scoreboard-team">
                    <Link
                      href={teamPath(game.awayTeam)}
                      className="flex justify-start items-center mt-2"
                    >
                      <div className="w-12 h-auto mr-5">
                        <Image
                          className="w-full h-full"
                          src={checkLogo(game.awayTeam.logo, 'club')}
                          alt={`Logo ${game.awayTeam.name}`}
                          objectFit="contain"
                          srcSet={[[100, maxScreenSize]]}
                        />
                      </div>
                      <div>{game.awayTeam.name}</div>
                    </Link>
                  </td>
                  <td className="tik3-scoreboard-score tik3-scoreboard-main-score text-primary">
                    <div className="bg-primary text-white rounded text-center px-2">
                      {game.awayGoals}
                    </div>
                  </td>
                </tr>
              </tbody>
            )}
            {game.state === GameState.POST && (
              <tbody>
                <tr className="tik3-scoreboard-row1">
                  <td className="tik3-scoreboard-team">
                    <Link
                      href={teamPath(game.homeTeam)}
                      className="flex justify-start items-center mb-2"
                    >
                      <div className="w-12 h-auto mr-5">
                        <Image
                          className="w-full h-full"
                          src={checkLogo(game.homeTeam.logo, 'club')}
                          alt={`Logo ${game.homeTeam.name}`}
                          objectFit="contain"
                          srcSet={[[100, maxScreenSize]]}
                        />
                      </div>
                      <div>{game.homeTeam.name}</div>
                    </Link>
                  </td>
                  <td className="tik3-scoreboard-score tik3-scoreboard-main-score">
                    <div className="bg-blue-dark text-white rounded text-center px-2">
                      {game.homeGoals}
                    </div>
                  </td>
                </tr>
                <tr className="tik3-scoreboard-row2">
                  <td className="tik3-scoreboard-team">
                    <Link
                      href={teamPath(game.awayTeam)}
                      className="flex justify-start items-center mt-2"
                    >
                      <div className="w-12 h-auto mr-5">
                        <Image
                          className="w-full h-full"
                          src={checkLogo(game.awayTeam.logo, 'club')}
                          alt={`Logo ${game.awayTeam.name}`}
                          objectFit="contain"
                          srcSet={[[100, maxScreenSize]]}
                        />
                      </div>
                      <div>{game.awayTeam.name}</div>
                    </Link>
                  </td>
                  <td className="tik3-scoreboard-score tik3-scoreboard-main-score">
                    <div className="bg-blue-dark text-white rounded text-center px-2">
                      {game.awayGoals}
                    </div>
                  </td>
                </tr>
              </tbody>
            )}
            {(game.state === GameState.POSTPONED ||
              game.state === GameState.CANCELED) && (
              <tbody>
                <tr className="tik3-scoreboard-row1">
                  <td className="tik3-scoreboard-team">
                    <Link
                      href={teamPath(game.homeTeam)}
                      className="flex justify-start items-center mb-2"
                    >
                      <div className="w-12 h-auto mr-5">
                        <Image
                          className="w-full h-full"
                          src={checkLogo(game.homeTeam.logo, 'club')}
                          alt={`Logo ${game.homeTeam.name}`}
                          objectFit="contain"
                          srcSet={[[100, maxScreenSize]]}
                        />
                      </div>
                      <div>{game.homeTeam.name}</div>
                    </Link>
                  </td>
                  <td className="tik3-scoreboard-score tik3-scoreboard-main-score">
                    {game.homeGoals}
                  </td>
                </tr>
                <tr className="tik3-scoreboard-row2">
                  <td className="tik3-scoreboard-team">
                    <Link
                      href={teamPath(game.awayTeam)}
                      className="flex justify-start items-center mt-2"
                    >
                      <div className="w-12 h-auto mr-5">
                        <Image
                          className="w-full h-full"
                          src={checkLogo(game.awayTeam.logo, 'club')}
                          alt={`Logo ${game.awayTeam.name}`}
                          objectFit="contain"
                          srcSet={[[100, maxScreenSize]]}
                        />
                      </div>
                      <div>{game.awayTeam.name}</div>
                    </Link>
                  </td>
                  <td className="tik3-scoreboard-score tik3-scoreboard-main-score">
                    {game.awayGoals}
                  </td>
                </tr>
              </tbody>
            )}
          </table>

          {/*
              // TODO : ausgabe (paragraph symbol? texte TBD)
              {game.extraStates && game.extraStates.length > 0 && (
                <span>{game.extraStates.join(', ')}</span>
              )}
          */}
        </div>
      </div>

      {isTablet && !hideGameStatusBtn && (
        <>
          <div className="mt-4">
            {game.state === GameState.LIVE && (
              <div className="bg-primary text-white mb-10 rounded px-3 py-2 uppercase inline-block">
                Jetzt live!
              </div>
            )}
            {game.state === GameState.POST && (
              <div className="bg-blue-dark text-white mb-10 rounded px-3 py-2 uppercase inline-block">
                Spiel beendet
              </div>
            )}
          </div>
        </>
      )}

      {!isTablet && !hideGameStatusBtn && (
        <div className="flex mb-4">
          {game.state === GameState.LIVE && (
            <div className="flex-none">
              <div className="bg-primary text-white mb-10 rounded px-3 py-2 uppercase inline-block">
                Jetzt live!
              </div>
            </div>
          )}
          {game.state === GameState.POST && (
            <div className="flex-none">
              <div className="bg-blue-dark text-white mb-10 rounded px-3 py-2 uppercase inline-block">
                Spiel beendet
              </div>
            </div>
          )}
        </div>
      )}

      <SpecialRatingInfo game={game} />
    </div>
  )
}
